import { useCallback, useEffect, useMemo, useState } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  Heading,
  Picker,
  PickerItem,
  ProgressCircle,
  ProgressBar,
  Text,
  Form,
  TextField,
  TextArea,
  Button,
  ButtonGroup,
  LinkButton,
  ActionButton,
  FileTrigger,
  Menu,
  MenuItem,
  MenuTrigger,
  SegmentedControl,
  SegmentedControlItem,
  Slider,
} from "@react-spectrum/s2";
import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import UploadToCloudIcon from "@react-spectrum/s2/icons/UploadToCloud";
import ImagesIcon from "@react-spectrum/s2/icons/Images";

import { listFoldersInDirectory, uploadFileByBlob } from "@app/services/s3";
import { store } from "@app/store";
import { useStore } from "zustand";
import { S3Image } from "@app/components/S3Image";
import { useZoltarMutation } from "@app/hooks/zoltar";
import {
  isZoltarGenerateOk,
  parseZoltarGenerateJson,
} from "@app/services/zoltar";
import {
  useDescribeMogrtTemplate,
  useRenderMogrtTemplate,
} from "@app/hooks/mogrtTemplates";
import { useVoices, useGenerateAudio } from "@app/hooks/textToSpeech";
import { useUploadFile } from "@app/hooks/s3";
import { listJobs } from "@app/services/jobs";
import { TextToSpeech } from "@app/components/TextToSpeech";


const MOGRT_ROOT_DIR = "mogrt/templates/";
const ACCEPTED_MEDIA_TYPES = ["image/jpeg", "image/png", "image/svg+xml"];

function getMogrtJobResultVideoUrl(job) {
  const outputs = job?.result?.outputs;
  if (!Array.isArray(outputs)) {
    return null;
  }
  for (const o of outputs) {
    const url = o?.destination?.url;
    if (typeof url === "string" && url.length > 0) {
      return url;
    }
  }
  return null;
}

export const MogrtAdmin = () => {
  useBreadcrumbs([
    { label: "Admin", href: "/admin" },
    { label: "Dynamic Graphics Render" },
  ]);

  const [folders, setFolders] = useState([]);
  const [foldersStatus, setFoldersStatus] = useState("idle");
  const [foldersError, setFoldersError] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [uploadingMedia, setUploadingMedia] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  const [customerName, setCustomerName] = useState("");
  const [campaignHint, setCampaignHint] = useState("");
  const [myMogrtJobs, setMyMogrtJobs] = useState([]);
  const [myMogrtJobsStatus, setMyMogrtJobsStatus] = useState("idle");
  const [myMogrtJobsError, setMyMogrtJobsError] = useState("");

  const email = useStore(store, (state) => state.ims?.profile?.email);

  const mogrtRootPath = selectedFolder
    ? `${MOGRT_ROOT_DIR}${selectedFolder}`
    : null;

  const describeMogrtTemplate = useDescribeMogrtTemplate(mogrtRootPath, {
    enabled: !!mogrtRootPath,
  });

  const renderMogrtTemplate = useRenderMogrtTemplate({
    jobSuccessStatuses: ["completed", "generated"],
  });
  const uploadFile = useUploadFile();
  const { data: voices } = useVoices();
  const generateAudio = useGenerateAudio();

  const hasAudioStep = selectedFolder === "AO";
  const renderStepIndex = hasAudioStep ? 4 : 3;

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setFoldersStatus("loading");
        setFoldersError("");
        const response = await listFoldersInDirectory(MOGRT_ROOT_DIR);
        const nextFolders = (response?.folders ?? []).sort((a, b) =>
          a.localeCompare(b),
        );
        setFolders(nextFolders);
        setFoldersStatus("success");
      } catch (error) {
        setFoldersStatus("error");
        setFoldersError(error?.message || "Failed to load folders");
      }
    };

    fetchFolders();
  }, []);

  const fetchMyMogrtJobs = useCallback(async () => {
    if (!email) {
      setMyMogrtJobs([]);
      setMyMogrtJobsStatus("idle");
      setMyMogrtJobsError("");
      return;
    }

    setMyMogrtJobsStatus("loading");
    setMyMogrtJobsError("");
    try {
      const data = await listJobs({ limit: 10 });
      setMyMogrtJobs(Array.isArray(data?.jobs) ? data.jobs : []);
      setMyMogrtJobsStatus("success");
    } catch (err) {
      setMyMogrtJobsError(err?.message || "Failed to load mogrt jobs");
      setMyMogrtJobsStatus("error");
    }
  }, [email]);

  useEffect(() => {
    fetchMyMogrtJobs();
  }, [fetchMyMogrtJobs]);

  // Initialise formValues from describe data, keyed by variableId
  useEffect(() => {
    if (!describeMogrtTemplate.isSuccess || !describeMogrtTemplate.data) {
      return;
    }

    const { controls, mediaFiles } = describeMogrtTemplate.data;

    const getMediaSourceForLabel = (label) => {
      if (!label) return null;
      const found = mediaFiles.find((filePath) => {
        const fileName = filePath.split("/").pop() || "";
        return fileName === label || fileName.split(".")[0] === label;
      });
      return found ? { path: found } : null;
    };

    const initialFormValues = controls.reduce((acc, control) => {
      if (control?.type === "text") {
        acc[control.variableId] = control?.defaultData?.text ?? "";
      } else if (control?.type === "media") {
        acc[control.variableId] =
          getMediaSourceForLabel(control?.label) ?? null;
      } else if (control?.type === "dropdown") {
        acc[control.variableId] =
          control?.defaultData?.selectedDropdownValue ?? "";
      } else if (control?.type === "slider") {
        const range = control?.range ?? {};
        acc[control.variableId] =
          control?.defaultData?.selectedSliderValue ?? range?.minimum ?? 0;
      }
      return acc;
    }, {});

    setFormValues(initialFormValues);
  }, [describeMogrtTemplate.isSuccess, describeMogrtTemplate.data]);

  const folderOptions = useMemo(
    () => folders.map((folder) => ({ id: folder, label: folder })),
    [folders],
  );

  const handleFolderChange = useCallback(
    (value) => {
      setSelectedFolder(value);
      setFormValues({});
      setUploadingMedia({});
      setCurrentStep(0);
      uploadFile.reset();
    },
    [uploadFile],
  );

  const handleRender = useCallback(() => {
    if (!describeMogrtTemplate.data) return;

    const emailPrefix = email?.split("@")?.[0];
    uploadFile.reset();

    renderMogrtTemplate.mutate(
      {
        ...describeMogrtTemplate.data,
        formValues,
      },
      {
        onSuccess: (outputUrl) => {
          fetchMyMogrtJobs();
          if (!emailPrefix || !selectedFolder) return;
          const filePath = `${emailPrefix}/mogrt/${selectedFolder}/${selectedFolder}.mp4`;
          uploadFile.mutate({ filePath, fileURL: outputUrl });
        },
      },
    );
  }, [
    describeMogrtTemplate.data,
    formValues,
    renderMogrtTemplate,
    uploadFile,
    email,
    selectedFolder,
    fetchMyMogrtJobs,
  ]);

  const handleSelectMedia = useCallback((controlId, filePath) => {
    setFormValues((prev) => ({
      ...prev,
      [controlId]: { path: filePath },
    }));
  }, []);

  const handleUploadMedia = useCallback(
    async (controlId, files) => {
      if (!files?.length) return;

      const [file] = files;
      const emailPrefix = email?.split("@")?.[0];

      if (!emailPrefix) {
        return;
      }

      try {
        setUploadingMedia((prev) => ({ ...prev, [controlId]: true }));

        const filePath = `${emailPrefix}/mogrt/${selectedFolder}/media/${file.name}`;
        await uploadFileByBlob(filePath, file);

        setFormValues((prev) => ({
          ...prev,
          [controlId]: { path: filePath },
        }));
      } catch {
        // upload errors surface via the UI
      } finally {
        setUploadingMedia((prev) => ({ ...prev, [controlId]: false }));
      }
    },
    [email, selectedFolder],
  );

  const zoltar = useZoltarMutation();
  const zoltarData = useMemo(() => {
    try {
      if (!isZoltarGenerateOk(zoltar.data)) return {};
      return parseZoltarGenerateJson(zoltar.data, "{}");
    } catch {
      return {};
    }
  }, [zoltar.data]);

  const handleCampaignGeneration = useCallback(() => {
    const promptTemplate = [
      {
        role: "user",
        prompt_template: `Customer name: ${customerName}${!!campaignHint ? `\n\[Hints]:\n${campaignHint}` : ""}`,
      },
      { role: "user", prompt_template: "Return only valid JSON response." },
    ];

    zoltar.mutate({ promptTemplate, systemPromptId: "UCX-texts" });
  }, [customerName, campaignHint, zoltar.mutate]);

  const handleVoiceOverGeneration = useCallback(
    async ({ text, voiceId, voiceConfig }) => {
      const uploadResult = await generateAudio.mutateAsync({
        text,
        voiceId,
        voiceConfig,
        email,
        selectedFolder,
      });

      const audioControl = describeMogrtTemplate.data?.controls?.find(
        (c) => c?.type === "audio",
      );
      if (audioControl) {
        setFormValues((prev) => ({
          ...prev,
          [audioControl.variableId]: { url: uploadResult.presignURL },
        }));
      }
    },
    [
      generateAudio.mutateAsync,
      email,
      selectedFolder,
      describeMogrtTemplate.data?.controls,
    ],
  );

  useEffect(() => {
    if (!Object.keys(zoltarData).length) return;

    const data = { ...zoltarData, "Company-name": customerName };
    const controls = describeMogrtTemplate.data?.controls ?? [];

    for (const [key, value] of Object.entries(data)) {
      const control = controls.find((ctrl) => ctrl.label === key);
      if (control) {
        setFormValues((prev) => ({
          ...prev,
          [control.variableId]: value,
        }));
      }
    }
  }, [zoltarData, customerName, describeMogrtTemplate.data?.controls]);

  const controls = describeMogrtTemplate.data?.controls ?? [];
  const mediaFiles = describeMogrtTemplate.data?.mediaFiles ?? [];
  const describeStatus = describeMogrtTemplate.isLoading
    ? "loading"
    : describeMogrtTemplate.isError
      ? "error"
      : describeMogrtTemplate.isSuccess
        ? "success"
        : "idle";

  return (
    <div
      className={style({
        display: "grid",
        gridTemplateAreas: ["steps", "content", "actions"],
        gridTemplateRows: ["auto", "minmax(0, 1fr)", "auto"],
        gap: 24,
        size: "full",
        paddingX: 24,
        paddingY: 12,
      })}
    >
      <SegmentedControl
        selectedKey={currentStep}
        onSelectionChange={setCurrentStep}
        disabledKeys={[1, 2, ...(hasAudioStep ? [3, 4] : [3])].filter(
          (step) => !selectedFolder || describeStatus !== "success",
        )}
        styles={style({ marginX: "auto" })}
      >
        <SegmentedControlItem id={0}>
          <Text styles={style({ display: "inline-flex", gap: 8 })}>
            <Text>①</Text> <Text>Select folder</Text>
          </Text>
        </SegmentedControlItem>
        <SegmentedControlItem id={1}>
          <Text styles={style({ display: "inline-flex", gap: 8 })}>
            <Text>②</Text> <Text>Project details</Text>
          </Text>
        </SegmentedControlItem>
        <SegmentedControlItem id={2}>
          <Text styles={style({ display: "inline-flex", gap: 8 })}>
            <Text>②</Text> <Text>Controls</Text>
          </Text>
        </SegmentedControlItem>
        {hasAudioStep && (
          <SegmentedControlItem id={3}>
            <Text styles={style({ display: "inline-flex", gap: 8 })}>
              <Text>③</Text> <Text>Audio</Text>
            </Text>
          </SegmentedControlItem>
        )}
        <SegmentedControlItem id={renderStepIndex}>
          <Text styles={style({ display: "inline-flex", gap: 8 })}>
            <Text>{hasAudioStep ? "④" : "③"}</Text> <Text>Render</Text>
          </Text>
        </SegmentedControlItem>
      </SegmentedControl>

      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          maxWidth: 960,
          marginX: "auto",
        })}
      >
        {currentStep === 0 && (
          <>
            <Heading styles={style({ font: "heading" })}>
              Select template to inspect and render controls.
            </Heading>
            <div
              className={style({
                display: "flex",
                flexDirection: "column",
                gap: 12,
                maxWidth: 640,
              })}
            >
              <Picker
                label="Mogrt folders"
                value={selectedFolder}
                onChange={handleFolderChange}
                isDisabled={foldersStatus === "loading"}
              >
                {folderOptions.map((folder) => (
                  <PickerItem id={folder.id} key={folder.id}>
                    {folder.label}
                  </PickerItem>
                ))}
              </Picker>

              {foldersStatus === "loading" && (
                <div
                  className={style({
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  })}
                >
                  <ProgressCircle aria-label="Loading folders" size="S" />
                  <Text>Loading folders…</Text>
                </div>
              )}

              {foldersStatus === "error" && <Text>{foldersError}</Text>}
            </div>
            <Heading styles={style({ font: "heading" })}>
              Your mogrt jobs
            </Heading>
            <Text styles={style({ font: "body-sm", color: "gray-700" })}>
              Jobs you started from this app (newest first). Links use the Mogrt
              output URL when available.
            </Text>
            {myMogrtJobsStatus === "loading" && (
              <div
                className={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                })}
              >
                <ProgressCircle aria-label="Loading jobs" size="S" />
                <Text>Loading jobs…</Text>
              </div>
            )}
            {myMogrtJobsStatus === "error" && <Text>{myMogrtJobsError}</Text>}
            {myMogrtJobsStatus === "success" && myMogrtJobs.length === 0 && (
              <Text>No mogrt jobs yet.</Text>
            )}
            {myMogrtJobsStatus === "success" && myMogrtJobs.length > 0 && (
              <ul
                className={style({
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                })}
              >
                {myMogrtJobs.map((job) => {
                  const resultUrl = getMogrtJobResultVideoUrl(job);
                  return (
                    <li
                      key={job.jobId}
                      className={style({
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 12,
                        padding: 12,
                        backgroundColor: "gray-100",
                        borderRadius: "default",
                      })}
                    >
                      <Text
                        styles={style({
                          font: "body-sm",
                          flexGrow: 1,
                          minWidth: 0,
                          wordBreak: "break-all",
                        })}
                        title={job.jobId}
                      >
                        {job.jobId}
                      </Text>
                      <Text styles={style({ font: "body-sm" })}>
                        {job.status ?? "—"}
                      </Text>
                      {resultUrl ? (
                        <LinkButton
                          href={resultUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="secondary"
                        >
                          <Text>Open result</Text>
                        </LinkButton>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {currentStep === 1 && (
          <>
            <div>
              <Heading styles={style({ font: "heading-xl" })}>
                Build your campaign story
              </Heading>
              <Text styles={style({ font: "body-xl" })}>
                Give us context about your company, audience and goal.
                <br />
                We'll generate the entire journey - personas, offers, channels
                and next actions.
              </Text>
            </div>

            <Form styles={style({ maxWidth: 460 })}>
              <Heading styles={style({ font: "heading" })}>
                Customer information
              </Heading>
              <TextField
                label="Brand name"
                description="Use the real client or brand name, not an internal project title."
                placeholder="SecurFinancial"
                isRequired
                value={customerName}
                onChange={setCustomerName}
              />
              <TextArea
                label="Hint"
                necessityIndicator="label"
                description="Share any context that might shape the message — audience, market, tone, or situation."
                placeholder="B2B software; APAC; procurement manager; sustainability angle; trade shows; 45-year-old father in Singapore."
                value={campaignHint}
                onChange={setCampaignHint}
              />
            </Form>
          </>
        )}

        {currentStep === 2 && (
          <>
            {zoltar.isPending && (
              <ProgressBar label="Generating campaign story" isIndeterminate />
            )}
            <Heading styles={style({ font: "heading" })}>
              Update controls and media files.
            </Heading>
            {describeStatus === "loading" && (
              <div
                className={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                })}
              >
                <ProgressCircle aria-label="Loading controls" size="S" />
                <Text>Loading controls…</Text>
              </div>
            )}

            {describeStatus === "error" && (
              <Text>{describeMogrtTemplate.error?.message}</Text>
            )}

            {describeStatus === "success" && controls.length === 0 && (
              <Text>No controls found.</Text>
            )}

            {controls.length > 0 && (
              <Form
                labelPosition="top"
                labelAlign="start"
                styles={style({ width: "100%" })}
              >
                {controls
                  .filter((control) => control?.type === "text")
                  .map((control) => (
                    <TextField
                      key={control.variableId}
                      label={control.label}
                      value={formValues[control.variableId] ?? ""}
                      onChange={(nextValue) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [control.variableId]: nextValue,
                        }))
                      }
                    />
                  ))}
                {controls
                  .filter((control) => control?.type === "dropdown")
                  .map((control) => {
                    const options = control?.options ?? {};
                    const optionItems = Object.entries(options).map(
                      ([id, label]) => ({ id, label }),
                    );
                    return (
                      <Picker
                        key={control.variableId}
                        label={control.label}
                        selectedKey={formValues[control.variableId] ?? ""}
                        onSelectionChange={(key) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [control.variableId]: key,
                          }))
                        }
                      >
                        {optionItems.map((item) => (
                          <PickerItem id={item.id} key={item.id}>
                            {item.label}
                          </PickerItem>
                        ))}
                      </Picker>
                    );
                  })}
                {controls
                  .filter((control) => control?.type === "slider")
                  .map((control) => {
                    const range = control?.range ?? {};
                    const min = range?.minimum ?? 0;
                    const max = range?.maximum ?? 100;
                    return (
                      <Slider
                        key={control.variableId}
                        label={control.label}
                        value={formValues[control.variableId] ?? min}
                        onChange={(value) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [control.variableId]: value,
                          }))
                        }
                        minValue={min}
                        maxValue={max}
                      />
                    );
                  })}
                <div
                  className={style({
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(220px, 1fr))",
                    gap: 12,
                    alignItems: "start",
                    justifyContent: "start",
                    width: "100%",
                  })}
                >
                  {controls
                    .filter((control) => control?.type === "media")
                    .map((control) => {
                      const selectedMediaPath =
                        formValues[control.variableId]?.path ?? "";
                      const isUploadingMedia =
                        uploadingMedia[control.variableId];

                      return (
                        <div
                          key={control.variableId}
                          className={style({
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            minWidth: 0,
                          })}
                        >
                          <Text>{control.label}</Text>
                          <div
                            className={style({
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              flexWrap: "wrap",
                            })}
                          >
                            <div
                              className={style({
                                width: 180,
                                height: 102,
                                borderRadius: "default",
                                overflow: "hidden",
                                backgroundColor: "gray-100",
                              })}
                            >
                              <S3Image
                                filePath={selectedMediaPath}
                                alt={control.label}
                                isLoading={
                                  describeStatus === "loading" ||
                                  isUploadingMedia
                                }
                                styles={style({
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  opacity: {
                                    isLoading: 0.4,
                                    default: 1,
                                  },
                                })({
                                  isLoading:
                                    describeStatus === "loading" ||
                                    isUploadingMedia,
                                })}
                              />
                            </div>
                            <ButtonGroup>
                              <MenuTrigger>
                                <ActionButton
                                  aria-label="Select media from S3"
                                  isDisabled={
                                    describeStatus === "loading" ||
                                    mediaFiles.length === 0
                                  }
                                >
                                  <ImagesIcon />
                                </ActionButton>
                                <Menu
                                  aria-label="Select media file"
                                  onAction={(filePath) =>
                                    handleSelectMedia(
                                      control.variableId,
                                      filePath,
                                    )
                                  }
                                >
                                  {mediaFiles.map((filePath) => (
                                    <MenuItem key={filePath} id={filePath}>
                                      {filePath.split("/").pop()}
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </MenuTrigger>
                              <FileTrigger
                                acceptedFileTypes={ACCEPTED_MEDIA_TYPES}
                                onSelect={(files) =>
                                  handleUploadMedia(control.variableId, files)
                                }
                              >
                                <ActionButton
                                  aria-label="Upload media from computer"
                                  isDisabled={isUploadingMedia}
                                >
                                  <UploadToCloudIcon />
                                </ActionButton>
                              </FileTrigger>
                            </ButtonGroup>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {describeStatus === "success" && mediaFiles.length === 0 && (
                  <Text>No media files found.</Text>
                )}
              </Form>
            )}
          </>
        )}

        {hasAudioStep && currentStep === 3 && (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 16,
              maxWidth: 1024,
            })}
          >
            <Text styles={style({ font: "heading" })}>Audio</Text>
            <TextToSpeech
              audioUrl={generateAudio.data?.presignURL}
              defaultText={`Please help me plan the ${formValues["0_10_text"]} campaign. Use last year’s campaign performance as the baseline. I’d like to target ${formValues["0_11_text"]}. Make sure the audience is at least ${formValues["0_12_slider"]} people. I’d like to test two new message variants for this campaign geared towards ${formValues["0_13_text"]}. Summarize results for me after a month and suggest improvements.`}
              voiceId="v123"
              voices={voices ?? []}
              onGenerate={handleVoiceOverGeneration}
              isPending={generateAudio.isPending}
              onVoiceSelect={() => {}}
            />
          </div>
        )}

        {currentStep === renderStepIndex && (
          <>
            {renderMogrtTemplate.isPending && (
              <ProgressBar
                label="Rendering video"
                isIndeterminate={!renderMogrtTemplate.progress}
                value={renderMogrtTemplate.progress}
              />
            )}
            {uploadFile.isPending && (
              <ProgressBar label="Uploading to S3" isIndeterminate />
            )}
            {renderMogrtTemplate.isError && (
              <div
                className={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                })}
              >
                <Text>{renderMogrtTemplate.error?.message}</Text>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    onPress={handleRender}
                    isDisabled={describeStatus !== "success"}
                  >
                    Retry
                  </Button>
                </ButtonGroup>
              </div>
            )}
            {uploadFile.isError && (
              <div
                className={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                })}
              >
                <Text>{uploadFile.error?.message}</Text>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    onPress={() =>
                      uploadFile.mutate({
                        filePath: `${email?.split("@")?.[0]}/mogrt/${selectedFolder}/${selectedFolder}.mp4`,
                        fileURL: renderMogrtTemplate.data,
                      })
                    }
                  >
                    Retry upload
                  </Button>
                </ButtonGroup>
              </div>
            )}
            {(uploadFile.data?.presignURL || renderMogrtTemplate.data) && (
              <video
                src={uploadFile.data?.presignURL ?? renderMogrtTemplate.data}
                controls
                className={style({
                  width: "100%",
                  height: "100%",
                  aspectRatio: "16 / 9",
                  borderRadius: "default",
                  backgroundColor: "gray-200",
                })}
              />
            )}
          </>
        )}

        <div className={style({ display: "flex", justifyContent: "center" })}>
          <ButtonGroup>
            <Button
              variant="secondary"
              onPress={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              isDisabled={currentStep === 0}
            >
              Previous
            </Button>
            <Button
              onPress={() => {
                if (currentStep === 1) {
                  handleCampaignGeneration();
                }

                if (currentStep === 2 && !hasAudioStep) {
                  handleRender();
                  setCurrentStep(renderStepIndex);
                  return;
                }

                if (currentStep === 3 && hasAudioStep) {
                  handleRender();
                  setCurrentStep(renderStepIndex);
                  return;
                }

                setCurrentStep((prev) => Math.min(prev + 1, renderStepIndex));
              }}
              isDisabled={
                (currentStep === 0 && !selectedFolder) ||
                (currentStep === 1 && customerName.trim() === "") ||
                (currentStep === 2 && describeStatus !== "success") ||
                currentStep === renderStepIndex
              }
            >
              Next
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};
